import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useContent, DEFAULT_CONTENT } from "../lib/content";
import { PREP_CATEGORIES } from "../lib/data";
import { Save, Plus, Trash2, ChevronUp, ChevronDown, Home, MessageCircle, ClipboardList, Route, Briefcase, CheckSquare, History, RotateCcw, X, Eye, Star, Calendar, FileText, Palette, Upload, Mail, Phone, BookOpen, LayoutGrid } from "lucide-react";
import { SectionedFormEditor } from "../components/admin/SectionedFormEditor";
import { ChecklistEditor } from "../components/admin/ChecklistEditor";
import { FormLibraryEditor } from "../components/admin/FormLibrary";
import { DEFAULT_TIMEZONE, browserTimezone, timezoneOptions, formatInZone } from "../lib/timezones";

const TABS = [
  { id: "branding", label: "Branding", icon: <Palette size={14} />, sides: ["sell", "buy"] },
  { id: "landing", label: "Landing", icon: <Home size={14} />, sides: ["sell"] },
  { id: "prep", label: "Prep Guide", icon: <ClipboardList size={14} />, sides: ["sell"] },
  { id: "dates", label: "Date Types", icon: <Calendar size={14} />, sides: ["sell", "buy"] },
  { id: "timeline", label: "Process", icon: <Route size={14} />, sides: ["sell", "buy"] },
  { id: "vendors", label: "Vendors", icon: <Briefcase size={14} />, sides: ["sell", "buy"] },
  { id: "closing", label: "Closing", icon: <CheckSquare size={14} />, sides: ["sell", "buy"] },
  { id: "buyerdash", label: "Dashboard", icon: <Home size={14} />, sides: ["buy"] },
  { id: "lender", label: "Lender", icon: <Phone size={14} />, sides: ["buy"] },
  { id: "resources", label: "Resources", icon: <BookOpen size={14} />, sides: ["buy"] },
  { id: "intake", label: "Intake Forms", icon: <ClipboardList size={14} />, sides: ["buy"] },
  { id: "forms", label: "Forms", icon: <FileText size={14} />, sides: ["sell"] },
  { id: "emails", label: "Emails", icon: <Mail size={14} />, sides: ["sell"] },
  { id: "referrals", label: "Referrals", icon: <Star size={14} />, sides: ["sell"] },
];

/* The sections, grouped. Ten peers in a row said nothing about what belonged
   with what, and the row wrapped badly as soon as the window narrowed. */
const OVERVIEW = "__overview";

const GROUPS = [
  { title: "Your brand", ids: ["branding"] },
  { title: "Your site", ids: ["landing", "buyerdash", "referrals"] },
  { title: "Client journey", ids: ["prep", "timeline", "dates", "closing", "lender", "resources"] },
  { title: "Resources & documents", ids: ["vendors", "forms", "intake", "emails"] },
];

const BLURB = {
  branding: "Logo, colour, your details",
  landing: "Hero, sections, what clients read first",
  prep: "The checklist you hand every seller",
  dates: "Which dates you track",
  timeline: "The steps of a sale, in your words",
  vendors: "Your directory",
  closing: "Closing-day checklist",
  buyerdash: "What buyers land on",
  lender: "Lender details and copy",
  resources: "Links and guides for buyers",
  intake: "What you ask buyers up front",
  forms: "Blank forms and where fields go",
  emails: "What goes out automatically",
  referrals: "What you ask for, and when",
};

/* Which slice of the content document each section owns, so the page can say
   "still the default" instead of making the agent open all fourteen to find
   out. Taken from what each editor actually reads and writes, including the
   field names the shared editors are handed per side.

   `forms` is deliberately absent. The form library lives in its own
   collection rather than in `content`, so comparing it against
   DEFAULT_CONTENT would always come back "default" -- which would be a lie
   rather than a missing feature. No key means no badge. */
function ownedKeys(id, side) {
  const buy = side === "buy";
  return ({
    branding: ["branding"],
    landing: ["landing"],
    prep: ["prep", "prep_blurbs"],
    dates: buy ? ["buyer_date_fields"] : ["date_fields", "under_contract_fields"],
    timeline: [buy ? "buyer_timeline" : "timeline"],
    vendors: ["vendors"],
    closing: [buy ? "buyer_closing" : "closing"],
    buyerdash: ["buyer_dashboard"],
    lender: ["lender"],
    resources: ["buyer_resources"],
    intake: ["buyer_intake_sections", "buyer_preferences_sections"],
    emails: ["emails"],
    referrals: ["referrals"],
  })[id] || [];
}

const STATE_TITLE = {
  edited: "You have changed this from the default",
  default: "Still exactly as it shipped",
  attention: "Something here still needs filling in",
};

const railItem = (on) => ({
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  width: "100%", padding: "8px 16px", fontSize: 13.5, textAlign: "left",
  cursor: "pointer", fontFamily: "inherit",
  background: on ? "var(--surface-alt)" : "transparent",
  color: on ? "var(--text)" : "var(--text-muted)",
  border: "none", borderLeft: `2px solid ${on ? "var(--primary)" : "transparent"}`,
});

export default function AdminContent() {
  const { user } = useAuth();
  const buyerFeaturesEnabled = !!user?.buyer_features_enabled;
  const { content, refresh } = useContent();
  // local deep-copied draft
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content)));
  const [tab, setTab] = useState(OVERVIEW);
  // Sell / Buy side selector — filters the tabs above and swaps the
  // fields that Timeline, Date Types, and Closing editors read/write.
  const [contentSide, setContentSide] = useState("sell");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [err, setErr] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [vLoading, setVLoading] = useState(false);

  // refresh draft when content hot-loads
  useEffect(() => { setDraft(JSON.parse(JSON.stringify(content))); }, [content]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(content), [draft, content]);

  /* The draft lives on this page, so moving between sections keeps it. What
     loses it is leaving, and nothing used to say so. */
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /* What is blank and shouldn't be. Only things the page can check honestly
     belong here -- a missing license number is a fact; "your copy could be
     better" is an opinion and would make the banner noise. */
  const attention = useMemo(() => {
    const b = draft.branding || {};
    const out = [];
    if (!b.license) {
      out.push({ tab: "branding",
                 text: "Your license number is blank \u2014 most states require it on listing advertising." });
    }
    if (!b.logo_data_url) {
      out.push({ tab: "branding",
                 text: "You haven't uploaded a logo, so clients see the default mark." });
    }
    return out;
  }, [draft]);

  const sectionState = (id) => {
    if (id === "branding" && attention.length) return "attention";
    const keys = ownedKeys(id, contentSide);
    if (!keys.length) return "";
    const changed = keys.some(
      (k) => JSON.stringify(draft[k]) !== JSON.stringify(DEFAULT_CONTENT[k]));
    return changed ? "edited" : "default";
  };

  const resetSection = () => {
    const keys = ownedKeys(tab, contentSide);
    const label = (TABS.find((t) => t.id === tab) || {}).label || "this section";
    if (!keys.length) {
      window.alert(`${label} isn't part of the content document, so there's nothing here to reset.`);
      return;
    }
    if (!window.confirm(`Put ${label} back to the default? Nothing else changes, `
                        + "and nothing is saved until you press Save changes.")) return;
    const next = { ...draft };
    keys.forEach((k) => {
      next[k] = DEFAULT_CONTENT[k] === undefined
        ? undefined
        : JSON.parse(JSON.stringify(DEFAULT_CONTENT[k]));
    });
    setDraft(next);
  };

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      await api.put("/admin/content", { content: draft });
      await refresh();
      setToast("Content saved. Sellers see it on their next refresh.");
      setTimeout(() => setToast(""), 3500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const resetAll = () => {
    if (!window.confirm("Reset ALL content to defaults? This can't be undone after you save.")) return;
    setDraft(JSON.parse(JSON.stringify(DEFAULT_CONTENT)));
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setVLoading(true);
    try {
      const { data } = await api.get("/admin/content/versions");
      setVersions(data.versions || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setVLoading(false); }
  };

  const restore = async (v) => {
    if (!window.confirm(`Restore content from ${new Date(v.created_at).toLocaleString()}? Your current state will be snapshotted first so you can undo.`)) return;
    try {
      await api.post(`/admin/content/versions/${v.id}/restore`);
      await refresh();
      setHistoryOpen(false);
      setToast("Restored. Site is now showing the previous version.");
      setTimeout(() => setToast(""), 3500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const previewAsSeller = (path = "/dashboard") => {
    try {
      localStorage.setItem("sellerhub_preview_content", JSON.stringify(draft));
    } catch {}
    window.open(`${path}?preview=1`, "_blank");
  };
  const [previewMenuOpen, setPreviewMenuOpen] = useState(false);

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <span className="overline">Admin · Content</span>
      <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        Edit your site.
      </h1>
      <p style={{ color: "var(--text-muted)", maxWidth: 680 }}>
        Change the copy, checklists, vendor directory, and process steps any time. Updates go live the next time a seller loads the page.
      </p>

      {toast && <div data-testid="content-toast" style={{ marginTop: 16, padding: "12px 16px", borderRadius: 12, background: "#E7F0EA", color: "var(--success)", fontSize: 14 }}>{toast}</div>}
      {err && <div data-testid="content-err" style={{ marginTop: 16, padding: "12px 16px", borderRadius: 12, background: "#F7E2E2", color: "var(--error)", fontSize: 14 }}>{err}</div>}

      {/* Controls. The side switch sits above the navigation it reshapes --
          it used to sit below, so the control deciding which sections exist
          lived underneath the list of them. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    gap: 12, flexWrap: "wrap", marginTop: 22 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {buyerFeaturesEnabled && (
          <div data-testid="content-side-switch" style={{ display: "inline-flex", gap: 3, background: "var(--surface-alt)", border: "1px solid var(--border)", borderRadius: 999, padding: 3 }}>
            {[
              { id: "sell", label: "SELL SIDE" },
              { id: "buy", label: "BUY SIDE" },
            ].map((opt) => {
              const active = contentSide === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`content-side-${opt.id}`}
                  onClick={() => {
                    setContentSide(opt.id);
                    // Sections differ per side, so a section that does not
                    // exist on the new one would leave the page blank. The
                    // overview always exists, and it shows what the new side
                    // actually has -- which is the question anyone switching
                    // sides is asking.
                    const cur = TABS.find((t) => t.id === tab);
                    if (!cur || !cur.sides.includes(opt.id)) setTab(OVERVIEW);
                  }}
                  style={{
                    border: "none", cursor: active ? "default" : "pointer",
                    padding: "5px 12px", borderRadius: 999,
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                    background: active ? "var(--primary)" : "transparent",
                    color: active ? "#fff" : "var(--text-muted)",
                    transition: "all 120ms ease",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <button
              data-testid="preview-as-seller-btn"
              className="btn-ghost"
              onClick={() => setPreviewMenuOpen((v) => !v)}
              style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <Eye size={14} /> {contentSide === "buy" ? "Preview as buyer" : "Preview as seller"} <ChevronDown size={12} />
            </button>
            {previewMenuOpen && (
              <>
                <div onClick={() => setPreviewMenuOpen(false)}
                     style={{ position: "fixed", inset: 0, zIndex: 49 }} />
                <div data-testid="preview-menu"
                     style={{
                       position: "absolute",
                       top: "calc(100% + 6px)",
                       right: 0,
                       minWidth: 180,
                       background: "var(--surface)",
                       border: "1px solid var(--border)",
                       borderRadius: 10,
                       boxShadow: "0 12px 32px -16px rgba(43,76,63,0.18)",
                       padding: 6,
                       zIndex: 50,
                       display: "flex",
                       flexDirection: "column",
                       gap: 2,
                     }}>
                  {(contentSide === "buy"
                    ? [
                        { path: "/", label: "Landing", testid: "preview-menu-landing" },
                        { path: "/login", label: "Login", testid: "preview-menu-login" },
                        { path: "/register", label: "Sign up", testid: "preview-menu-register" },
                        { path: "/buyer", label: "Buyer dashboard", testid: "preview-menu-dashboard" },
                      ]
                    : [
                        { path: "/", label: "Landing", testid: "preview-menu-landing" },
                        { path: "/login", label: "Login", testid: "preview-menu-login" },
                        { path: "/register", label: "Sign up", testid: "preview-menu-register" },
                        { path: "/dashboard", label: "Seller dashboard", testid: "preview-menu-dashboard" },
                      ]
                  ).map((o) => (
                    <button key={o.path}
                            data-testid={o.testid}
                            onClick={() => { setPreviewMenuOpen(false); previewAsSeller(o.path); }}
                            style={{
                              background: "transparent",
                              border: "none",
                              textAlign: "left",
                              padding: "8px 12px",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 13,
                              color: "var(--text)",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "var(--surface-alt)"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button data-testid="content-history-btn" className="btn-ghost" onClick={openHistory} style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <History size={14} /> History
          </button>
        </div>
      </div>

      {/* The save bar. Unsaved used to be a small dot among eight other
          controls; moving between sections keeps the draft, so the only way
          to lose it is to leave the page -- which is now also guarded. */}
      <div style={{ position: "sticky", top: 72, zIndex: 10, background: "var(--bg)", padding: "14px 0", borderBottom: "1px solid var(--border)", margin: "16px 0 0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div data-testid="content-dirty-state" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}>
            {dirty ? (
              <>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--secondary)", display: "inline-block" }} />
                <b style={{ fontWeight: 500, color: "var(--secondary)" }}>Unsaved changes</b>
                <span style={{ color: "var(--text-muted)" }}>&mdash; your clients still see the old version.</span>
              </>
            ) : (
              <span style={{ color: "var(--text-muted)" }}>Everything here is saved and live.</span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {tab === OVERVIEW ? (
              <button data-testid="content-reset-btn" className="btn-ghost" onClick={resetAll} style={{ fontSize: 13 }}>Reset everything</button>
            ) : (
              <button data-testid="content-reset-section-btn" className="btn-ghost" onClick={resetSection} style={{ fontSize: 13 }}>
                <RotateCcw size={13} /> Reset {(TABS.find((t) => t.id === tab) || {}).label || "this section"}
              </button>
            )}
            <button data-testid="content-save-btn" className="btn-primary" onClick={save} disabled={busy || !dirty}>
              <Save size={14} /> {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      {attention.length > 0 && (
        <div data-testid="content-attention" style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 20, padding: "14px 16px", borderRadius: 12, background: "rgba(184,92,71,0.08)", border: "1px solid rgba(184,92,71,0.22)", fontSize: 13.5, lineHeight: 1.55 }}>
          <div style={{ fontSize: 15, lineHeight: 1.2 }}>&#9888;</div>
          <div>
            <b style={{ fontWeight: 500, color: "var(--secondary)" }}>
              {attention.length === 1 ? "One thing isn't set up yet." : `${attention.length} things aren't set up yet.`}
            </b>
            <br />
            {attention.map((a) => a.text).join(" ")}{" "}
            <button type="button" data-testid="content-attention-go"
                    onClick={() => setTab(attention[0].tab)}
                    style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "var(--secondary)", textDecoration: "underline", fontSize: 13.5 }}>
              Go and fix it
            </button>
          </div>
        </div>
      )}

      {historyOpen && (
        <HistoryDrawer versions={versions} loading={vLoading} onClose={() => setHistoryOpen(false)} onRestore={restore} />
      )}

      <div style={{ display: "flex", gap: 22, alignItems: "flex-start", flexWrap: "wrap", marginTop: 20 }}>
        {/* The rail. Ten tabs in a row said nothing about what belonged with
            what and wrapped badly the moment the window narrowed; a column
            has room for the state of each section beside its name, and one
            more section costs a line rather than horizontal space. */}
        {/* Deliberately not sticky: with fourteen sections the rail can be
            taller than a short window, and a pinned column you cannot scroll
            to the bottom of is worse than one that scrolls with the page. */}
        <div className="card" style={{ flex: "0 0 236px", padding: "8px 0 14px" }} data-testid="content-rail">
          <button type="button" data-testid="ctab-overview" onClick={() => setTab(OVERVIEW)}
                  style={{ ...railItem(tab === OVERVIEW), fontWeight: 500 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <LayoutGrid size={14} /> Overview
            </span>
          </button>
          {GROUPS.map((g) => {
            const items = g.ids
              .map((id) => TABS.find((t) => t.id === id))
              .filter((t) => t && t.sides.includes(contentSide));
            if (!items.length) return null;
            return (
              <div key={g.title}>
                <div className="overline" style={{ margin: "16px 0 6px 16px" }}>{g.title}</div>
                {items.map((t) => {
                  const st = sectionState(t.id);
                  return (
                    <button key={t.id} type="button" data-testid={`ctab-${t.id}`}
                            onClick={() => setTab(t.id)} style={railItem(tab === t.id)}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        {t.icon} {t.label}
                      </span>
                      {st && (
                        <span data-testid={`cstate-${t.id}`} title={STATE_TITLE[st]}
                              style={{ width: 6, height: 6, borderRadius: "50%", flex: "0 0 auto",
                                       background: st === "attention" ? "var(--secondary)"
                                         : st === "edited" ? "var(--primary)" : "transparent",
                                       border: st === "default" ? "1px solid var(--border)" : "none" }} />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
          <div style={{ margin: "14px 16px 0", paddingTop: 12, borderTop: "1px solid var(--border)", fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.7 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--primary)", display: "inline-block", marginRight: 6 }} />edited<br />
            <span style={{ width: 6, height: 6, borderRadius: "50%", border: "1px solid var(--border)", display: "inline-block", marginRight: 6 }} />still the default
          </div>
        </div>

        <div style={{ flex: "1 1 560px", minWidth: 0, display: "flex", flexDirection: "column", gap: 20 }}>
          {tab === OVERVIEW && (
            <div data-testid="content-overview" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 14 }}>
              {TABS.filter((t) => t.sides.includes(contentSide)).map((t) => {
                const st = sectionState(t.id);
                const chip = st === "attention"
                  ? { label: "Needs attention", bg: "rgba(184,92,71,0.12)", fg: "var(--secondary)" }
                  : st === "edited"
                    ? { label: "Edited", bg: "rgba(43,76,63,0.10)", fg: "var(--primary)" }
                    : st === "default"
                      ? { label: "Default", bg: "var(--surface-alt)", fg: "var(--text-muted)" }
                      : null;
                return (
                  <button key={t.id} type="button" className="card" data-testid={`ccard-${t.id}`}
                          onClick={() => setTab(t.id)}
                          style={{ padding: "18px 20px", textAlign: "left", cursor: "pointer",
                                   border: "1px solid var(--border)", background: "var(--surface)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                      <span style={{ fontWeight: 500, fontSize: 14.5, display: "inline-flex", alignItems: "center", gap: 7 }}>
                        {t.icon} {t.label}
                      </span>
                      {chip && (
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap", background: chip.bg, color: chip.fg }}>
                          {chip.label}
                        </span>
                      )}
                    </div>
                    <div style={{ color: "var(--text-muted)", fontSize: 12.5, marginTop: 6, lineHeight: 1.45 }}>
                      {BLURB[t.id] || ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {tab === "branding" && <><TimezoneCard /><BrandingEditor draft={draft} setDraft={setDraft} /></>}
          {tab === "landing" && <LandingEditor draft={draft} setDraft={setDraft} />}
          {tab === "prep" && <PrepEditor draft={draft} setDraft={setDraft} />}
          {tab === "dates" && <DatesTab draft={draft} setDraft={setDraft} field={contentSide === "buy" ? "buyer_date_fields" : "date_fields"} showUnderContract={contentSide !== "buy"} />}
          {tab === "timeline" && <TimelineEditor draft={draft} setDraft={setDraft} field={contentSide === "buy" ? "buyer_timeline" : "timeline"} />}
          {tab === "vendors" && <VendorsEditor draft={draft} setDraft={setDraft} />}
          {tab === "closing" && <ClosingEditor draft={draft} setDraft={setDraft} field={contentSide === "buy" ? "buyer_closing" : "closing"} title={contentSide === "buy" ? "Buyer closing-day checklist" : "Closing-day checklist"} />}
          {tab === "buyerdash" && <DashboardCopyEditor draft={draft} setDraft={setDraft} />}
          {tab === "lender" && <LenderEditor draft={draft} setDraft={setDraft} />}
          {tab === "resources" && <ResourcesEditor draft={draft} setDraft={setDraft} />}
          {tab === "intake" && <IntakeFormsEditor draft={draft} setDraft={setDraft} />}
          {tab === "forms" && (
            <>
              <FormLibraryEditor />
              <FormsEditor draft={draft} setDraft={setDraft} />
              <BuyerInfoFieldsEditor draft={draft} setDraft={setDraft} />
            </>
          )}
          {tab === "referrals" && <ReferralsEditor draft={draft} setDraft={setDraft} />}
          {tab === "emails" && <EmailsEditor draft={draft} setDraft={setDraft} />}
        </div>
      </div>
    </div>
  );
}

/* ---------- History drawer ---------- */
function HistoryDrawer({ versions, loading, onClose, onRestore }) {
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(28,25,23,0.45)", zIndex: 50 }} />
      <div style={{ position: "fixed", top: 0, right: 0, height: "100vh", width: "min(440px, 100vw)", background: "var(--bg)", borderLeft: "1px solid var(--border)", boxShadow: "-24px 0 60px rgba(0,0,0,0.12)", zIndex: 51, display: "flex", flexDirection: "column" }} data-testid="history-drawer">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid var(--border)" }}>
          <div>
            <span className="overline">Version history</span>
            <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "4px 0 0", letterSpacing: "-0.01em" }}>Recent saves</h3>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="history-close-btn"><X size={18} /></button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 32px" }}>
          {loading ? (
            <div style={{ color: "var(--text-muted)", padding: 16 }}>Loading…</div>
          ) : versions.length === 0 ? (
            <div style={{ color: "var(--text-muted)", padding: 16 }} data-testid="history-empty">
              No past versions yet. Every save from now on will show up here with a one-click restore.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {versions.map((v, i) => {
                const d = new Date(v.created_at);
                const pv = v.preview || {};
                return (
                  <div key={v.id} className="card" style={{ padding: 16 }} data-testid={`version-${i}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{d.toLocaleString()}</div>
                        {v.created_by && <div style={{ color: "var(--text-muted)", fontSize: 12 }}>by {v.created_by}</div>}
                        {v.note && <div style={{ color: "var(--text-muted)", fontSize: 12, fontStyle: "italic", marginTop: 2 }}>{v.note}</div>}
                      </div>
                      <button className="btn-secondary" style={{ padding: "6px 12px", fontSize: 13 }} onClick={() => onRestore(v)} data-testid={`restore-btn-${i}`}>
                        <RotateCcw size={12} /> Restore
                      </button>
                    </div>
                    <div style={{ marginTop: 10, color: "var(--text-muted)", fontSize: 12, lineHeight: 1.5 }}>
                      {pv.landing_emphasis && <>Accent word: <b style={{ color: "var(--text)" }}>{pv.landing_emphasis}</b> · </>}
                      {pv.repairs_count ?? 0} repairs · {pv.purges_count ?? 0} purges · {pv.photo_prep_count ?? 0} photo prep · {pv.timeline_count ?? 0} stages · {pv.vendors_count ?? 0} vendors · {pv.closing_count ?? 0} closing
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}


/* ---------- Branding editor ---------- */
const SWATCHES = [
  { name: "Forest (default)", value: "#2B4C3F" },
  { name: "Deep Navy", value: "#1E3A5F" },
  { name: "Wine", value: "#6B2C39" },
  { name: "Terracotta", value: "#B85C47" },
  { name: "Charcoal", value: "#1C1917" },
  { name: "Sage", value: "#6B8E7F" },
  { name: "Rust", value: "#8B4513" },
  { name: "Plum", value: "#5D3A5D" },
];

/**
 * Timezone lives on the agent's own user record, not in site_content, so this
 * card saves itself rather than riding along with the Branding draft.
 *
 * It matters more than it looks: showing times are stored as plain date + time
 * strings, so "2 PM" is only a real moment once we know whose 2 PM it is. Get
 * this wrong and every seller reminder fires at the wrong hour.
 */
function TimezoneCard() {
  const { user, updateProfile } = useAuth();
  const current = user?.timezone || DEFAULT_TIMEZONE;
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { setValue(current); }, [current]);

  const browser = browserTimezone();
  const mismatch = browser && browser !== value;

  const save = async (tz) => {
    setValue(tz);
    setSaving(true);
    setMsg("");
    const res = await updateProfile({ timezone: tz });
    setSaving(false);
    if (res.ok) {
      setMsg("Saved.");
      setTimeout(() => setMsg(""), 2500);
    } else {
      setValue(current);            // roll back rather than show a lie
      setMsg(res.error || "Couldn't save that.");
    }
  };

  const sample = formatInZone(new Date(), value, { weekday: "short", hour: "numeric", minute: "2-digit" });

  return (
    <div className="card" style={{ padding: 28, marginBottom: 20 }} data-testid="timezone-card">
      <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Your timezone</h2>
      <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 18px", maxWidth: 620 }}>
        Showing reminders go out on your local clock. When you schedule a showing for 2:00 PM,
        this is the 2:00 PM we mean.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <select
          data-testid="timezone-select"
          className="input-field"
          style={{ maxWidth: 320 }}
          value={value}
          disabled={saving}
          onChange={(e) => save(e.target.value)}
        >
          {timezoneOptions(value).map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
        {sample && (
          <span style={{ fontSize: 13.5, color: "var(--text-muted)" }}>
            Right now that's <b style={{ color: "var(--text)" }}>{sample}</b>
          </span>
        )}
        {msg && <span style={{ fontSize: 13.5, color: "var(--primary)" }}>{msg}</span>}
      </div>

      {mismatch && (
        <div style={{ marginTop: 14, fontSize: 13.5, color: "var(--text-muted)" }}>
          This computer says you're in <b>{browser.replace(/_/g, " ")}</b>.{" "}
          <button type="button" className="btn-ghost" onClick={() => save(browser)}
                  style={{ padding: 0, color: "var(--primary)", fontWeight: 600, fontSize: 13.5 }}
                  data-testid="timezone-use-browser">
            Use that instead
          </button>
        </div>
      )}
    </div>
  );
}

function BrandingEditor({ draft, setDraft }) {
  const defaults = DEFAULT_CONTENT.branding || {};
  const b = draft.branding || defaults;
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = React.useRef(null);

  const set = (patch) => setDraft({ ...draft, branding: { ...b, ...patch } });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr("");
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      setUploadErr("Use a PNG, JPG, SVG, or WEBP.");
      return;
    }
    if (file.size > 1_500_000) {
      setUploadErr("Logo is too big (max 1.5 MB). Try resizing or using an SVG.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set({ logo_data_url: reader.result });
    reader.onerror = () => setUploadErr("Couldn't read that file. Try a different one.");
    reader.readAsDataURL(file);
  };

  const color = b.primary_color || "#2B4C3F";

  return (
    <div className="card" style={{ padding: 28 }} data-testid="branding-editor">
      <div style={{ marginBottom: 22 }}>
        <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Branding</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>
          Your logo, product name, agent details, and primary color — applied across the app, emails, and PDFs.
          Each field falls back to the default if left blank.
        </p>
      </div>

      <div style={{ marginBottom: 24, padding: "16px 20px", borderRadius: 12, background: "#FBFAF6", border: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: color, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
          {b.logo_data_url ? (
            <img src={b.logo_data_url} alt="logo preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <Home size={20} color="#fff" />
          )}
        </div>
        <div>
          <div className="font-serif" style={{ fontSize: 22, fontWeight: 500 }}>{b.product_name || defaults.product_name}</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Preview — this is how the header will render for sellers.
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <L label="Product name" hint="Your portal is always named PorchLight. Contact support if you'd like to discuss a custom-labeled plan.">
          <input
            data-testid="brand-product-name"
            className="input-field"
            value="PorchLight"
            readOnly
            disabled
            style={{ backgroundColor: "#F4EFE3", color: "var(--text-muted)", cursor: "not-allowed" }}
          />
        </L>
        <L label="Firm / Brokerage" hint="Appears in onboarding, invite emails, and PDFs.">
          <input data-testid="brand-firm" className="input-field" value={b.firm || ""} onChange={(e) => set({ firm: e.target.value })} placeholder="Your brokerage" />
        </L>
        <L label="Agent full name" hint="Used in 'contact your agent' cards and email signatures.">
          <input data-testid="brand-agent-name" className="input-field" value={b.agent_name || ""} onChange={(e) => set({ agent_name: e.target.value })} placeholder="Your full name" />
        </L>
        <L label="License number" hint="Most states require it on listing advertising. Set it once and it fills the footer on every social campaign you build.">
          <input data-testid="brand-license" className="input-field" value={b.license || ""} onChange={(e) => set({ license: e.target.value })} placeholder="e.g. 01234567" />
        </L>
        <L label="Agent first name" hint="Used where a first name is friendlier — e.g. 'Ask {first_name}'.">
          <input data-testid="brand-agent-first-name" className="input-field" value={b.agent_first_name || ""} onChange={(e) => set({ agent_first_name: e.target.value })} placeholder="First name only" />
        </L>
        <L label="Agent email" hint="Reply-to on all outgoing emails and the 'mailto:' link on seller pages.">
          <input data-testid="brand-agent-email" className="input-field" type="email" value={b.agent_email || ""} onChange={(e) => set({ agent_email: e.target.value })} placeholder="you@yourbrokerage.com" />
        </L>
        <L label="Agent phone" hint="Shown on the Ask, Invite, and Documents pages.">
          <input data-testid="brand-agent-phone" className="input-field" value={b.agent_phone || ""} onChange={(e) => set({ agent_phone: e.target.value })} placeholder="(555) 123-4567" />
        </L>
        {/* Sender email field hidden for now — exposing it risks agents entering
            a non-verified Resend domain and their sellers silently not getting
            emails. Will re-introduce as a read-only "contact support to set up
            your own sending domain" link once we have a proper per-agent Resend
            domain verification flow. Data field still exists in site_content
            so anything set via direct DB edit keeps working. */}
        {false && (
        <L label="Sender email (optional)" hint="The 'From:' address on emails your sellers receive. Must be a verified domain in Resend. Leave blank to use the PorchLight default.">
          <input data-testid="brand-sender-email" className="input-field" type="email" value={b.sender_email || ""} onChange={(e) => set({ sender_email: e.target.value })} placeholder="e.g. hello@yourbrand.com" />
        </L>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Primary color</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
          Drives buttons, links, the header icon, and email accents. Pick a preset or paste a hex.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          {SWATCHES.map((s) => {
            const selected = (b.primary_color || "#2B4C3F").toLowerCase() === s.value.toLowerCase();
            return (
              <button
                key={s.value}
                type="button"
                data-testid={`brand-swatch-${s.value.replace('#','').toLowerCase()}`}
                onClick={() => set({ primary_color: s.value })}
                title={s.name}
                style={{
                  width: 40, height: 40, borderRadius: 10, background: s.value,
                  border: selected ? "3px solid var(--text)" : "1px solid var(--border)",
                  cursor: "pointer",
                }}
              />
            );
          })}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 8 }}>
            <input
              data-testid="brand-color-picker"
              type="color"
              value={color}
              onChange={(e) => set({ primary_color: e.target.value })}
              style={{ width: 40, height: 40, border: "1px solid var(--border)", borderRadius: 10, cursor: "pointer", padding: 0, background: "transparent" }}
            />
            <input
              data-testid="brand-color-hex"
              className="input-field"
              value={b.primary_color || ""}
              onChange={(e) => set({ primary_color: e.target.value })}
              placeholder="#2B4C3F"
              style={{ width: 140, fontFamily: "monospace" }}
            />
            {b.primary_color && (
              <button type="button" className="btn-ghost" style={{ fontSize: 12 }} onClick={() => set({ primary_color: "" })} data-testid="brand-color-clear">Reset</button>
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Logo</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
          Shown in the header. PNG, JPG, SVG, or WEBP up to 1.5&nbsp;MB. A square or wide horizontal logo works best.
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ width: 72, height: 72, borderRadius: 14, background: color, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", border: "1px solid var(--border)" }}>
            {b.logo_data_url ? (
              <img src={b.logo_data_url} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            ) : (
              <Home size={28} color="#fff" />
            )}
          </div>
          <input ref={fileRef} data-testid="brand-logo-file" type="file" accept="image/*" style={{ display: "none" }} onChange={onLogo} />
          <button type="button" className="btn-secondary" onClick={() => fileRef.current?.click()} data-testid="brand-logo-upload">
            <Upload size={14} /> {b.logo_data_url ? "Replace logo" : "Upload logo"}
          </button>
          {b.logo_data_url && (
            <button type="button" className="btn-ghost" style={{ color: "var(--error)" }} onClick={() => set({ logo_data_url: "" })} data-testid="brand-logo-clear">
              <Trash2 size={14} /> Remove
            </button>
          )}
        </div>
        {uploadErr && <div style={{ marginTop: 10, color: "var(--error)", fontSize: 13 }} data-testid="brand-logo-err">{uploadErr}</div>}
      </div>

      {/* Email signature */}
      <div style={{ marginTop: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Email signature</div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12 }}>
          Appended to the bottom of every email the app sends on your behalf (form reminders, date
          updates, etc.). Plain text or HTML both work — if you already have a signature in Gmail or
          Outlook, copy the HTML and paste it here. Leave blank to use a simple one-liner generated
          from your name and firm.
        </div>
        <textarea
          data-testid="brand-email-signature"
          className="input-field"
          rows={8}
          value={b.email_signature || ""}
          onChange={(e) => set({ email_signature: e.target.value })}
          placeholder={`e.g.\n\nJordan Reese\nReese Realty, Licensed Realtor\n(555) 123-4567 | jordan@reeserealty.com`}
          style={{ fontFamily: "inherit", fontSize: 13, lineHeight: 1.5 }}
        />
        {b.email_signature && (
          <div style={{ marginTop: 14, padding: "14px 18px", borderRadius: 10, background: "#FBFAF6", border: "1px solid var(--border)" }}>
            <div className="overline" style={{ color: "var(--text-muted)", marginBottom: 8 }}>Preview</div>
            <div
              data-testid="brand-email-signature-preview"
              style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}
              dangerouslySetInnerHTML={{ __html: b.email_signature }}
            />
          </div>
        )}
      </div>
    </div>
  );
}


/* ---------- Landing editor ---------- */
function LandingEditor({ draft, setDraft }) {
  const l = draft.landing || {};
  const set = (k) => (e) => setDraft({ ...draft, landing: { ...l, [k]: e.target.value } });

  const setTip = (i) => (e) => {
    const tips = [...(l.tips || [])];
    tips[i] = e.target.value;
    setDraft({ ...draft, landing: { ...l, tips } });
  };
  const addTip = () => setDraft({ ...draft, landing: { ...l, tips: [...(l.tips || []), "New tip"] } });
  const removeTip = (i) => setDraft({ ...draft, landing: { ...l, tips: (l.tips || []).filter((_, j) => j !== i) } });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="card" style={{ padding: 28 }}>
        <div className="overline">Hero</div>
        <div style={{ display: "flex", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 340px", minWidth: 0 }}>
            <L label="Overline" hint="The small uppercase line above the headline">
              <input data-testid="landing-overline" className="input-field" value={l.hero_overline || ""} onChange={set("hero_overline")} />
            </L>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 14 }}>
              <L label="Headline">
                <input data-testid="landing-line1" className="input-field" value={l.hero_line_1 || ""} onChange={set("hero_line_1")} />
              </L>
              <L label="Accent">
                <input data-testid="landing-emphasis" className="input-field" value={l.hero_emphasis || ""} onChange={set("hero_emphasis")} />
              </L>
              <L label="&nbsp;">
                <input data-testid="landing-line2" className="input-field" value={l.hero_line_2 || ""} onChange={set("hero_line_2")} />
              </L>
            </div>
            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 6 }}>
              The accent word renders in italic, in your brand colour.
            </div>
            <L label="Subheading" style={{ marginTop: 14 }}>
              <textarea data-testid="landing-sub" className="input-field" rows={3} value={l.hero_sub || ""} onChange={set("hero_sub")} />
            </L>
          </div>

          {/* Three boxes spelling one sentence, labelled "before the accent
              word" and "after the accent word", meant writing it blind.
              Assembling it costs nothing and answers the question. */}
          <div style={{ flex: "1 1 280px", minWidth: 0 }}>
            <div className="overline">As clients will see it</div>
            <div data-testid="landing-hero-preview"
                 style={{ border: "1px solid var(--border)", borderRadius: 11, padding: "22px 20px", marginTop: 8, background: "var(--surface-alt)" }}>
              <div className="overline">{l.hero_overline || ""}</div>
              <div className="font-serif" style={{ fontSize: 30, lineHeight: 1.15, marginTop: 8, fontWeight: 500 }}>
                {l.hero_line_1 || ""}{" "}
                <i style={{ color: "var(--secondary)" }}>{l.hero_emphasis || ""}</i>{" "}
                {l.hero_line_2 || ""}
              </div>
              {l.hero_sub && (
                <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 10, lineHeight: 1.55 }}>{l.hero_sub}</p>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 6 }}>Updates as you type.</div>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="overline">Quick seller tips</div>
          <button type="button" className="btn-secondary" onClick={addTip} data-testid="tips-add"><Plus size={14} /> Add tip</button>
        </div>
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          {(l.tips || []).map((t, i) => (
            <div key={i} style={{ display: "flex", gap: 8 }} data-testid={`tip-row-${i}`}>
              <input className="input-field" value={t} onChange={setTip(i)} data-testid={`tip-input-${i}`} />
              <button type="button" className="btn-ghost" onClick={() => removeTip(i)} data-testid={`tip-remove-${i}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Prep editor ---------- */
function PrepEditor({ draft, setDraft }) {
  const blurbs = draft.prep_blurbs || {};
  const setBlurb = (id, value) => setDraft({ ...draft, prep_blurbs: { ...blurbs, [id]: value } });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {PREP_CATEGORIES.map((s) => (
        <div key={s.id} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="card" style={{ padding: 20 }} data-testid={`prep-blurb-${s.id}`}>
            <div className="overline" style={{ marginBottom: 6 }}>{s.label} · description</div>
            <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "0 0 10px" }}>
              The short subtitle that appears under "{s.label}" on the seller's Prep Guide page.
            </p>
            <textarea
              data-testid={`prep-blurb-input-${s.id}`}
              className="input-field"
              rows={2}
              value={blurbs[s.id] ?? ""}
              onChange={(e) => setBlurb(s.id, e.target.value)}
              placeholder={`e.g. ${DEFAULT_CONTENT.prep_blurbs?.[s.id] || "A short, warm description of what to focus on in this section."}`}
            />
          </div>
          <ChecklistEditor
            title={s.label}
            testidPrefix={`prep-${s.id}`}
            items={draft.prep?.[s.id] || []}
            onChange={(items) => setDraft({ ...draft, prep: { ...draft.prep, [s.id]: items } })}
            idPrefix={s.id[0]}
          />
        </div>
      ))}
    </div>
  );
}

/* ---------- Date Types tab (wraps Important Dates + Under-contract editors) ---------- */
function DatesTab({ draft, setDraft, field = "date_fields", showUnderContract = true }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <DateTypesEditor draft={draft} setDraft={setDraft} field={field} />
      {showUnderContract && <UnderContractFieldsEditor draft={draft} setDraft={setDraft} />}
    </div>
  );
}

/* ---------- Under-contract milestones editor ---------- */
function UnderContractFieldsEditor({ draft, setDraft }) {
  const fields = draft.under_contract_fields || [];
  const update = (i, patch) => setDraft({
    ...draft,
    under_contract_fields: fields.map((f, j) => j === i ? { ...f, ...patch } : f),
  });
  const remove = (i) => setDraft({
    ...draft,
    under_contract_fields: fields.filter((_, j) => j !== i),
  });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, under_contract_fields: next });
  };
  const slugify = (label) => (label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || `uc_${Date.now().toString(36).slice(-4)}`;
  const add = () => {
    const usedKeys = new Set(fields.map((f) => f.key));
    let baseKey = "new_milestone";
    let key = baseKey;
    let n = 2;
    while (usedKeys.has(key)) { key = `${baseKey}_${n}`; n += 1; }
    setDraft({
      ...draft,
      under_contract_fields: [...fields, { key, label: "New Milestone", hasTime: false }],
    });
  };

  return (
    <div className="card" style={{ padding: 28, maxWidth: 760 }} data-testid="under-contract-fields-editor">
      <div className="overline">Under-contract milestones</div>
      <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, maxWidth: 600 }}>
        Add, rename, reorder, or remove the contract deadlines that appear in <b>Admin → Sellers → Under contract</b> and on every seller's "Contract milestones" card. Toggle "Has time" for milestones where the time of day matters (inspection appointments, appraisal visits).
      </p>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        {fields.map((f, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto auto", gap: 10, alignItems: "end" }} data-testid={`uc-field-row-${i}`}>
            <div>
              {i === 0 && <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Display label</label>}
              <input
                data-testid={`uc-field-label-${i}`}
                className="input-field"
                value={f.label || ""}
                onChange={(e) => {
                  const newLabel = e.target.value;
                  const shouldAutoKey = (f.label === "New Milestone" || !f.key);
                  update(i, { label: newLabel, ...(shouldAutoKey ? { key: slugify(newLabel) } : {}) });
                }}
                placeholder="e.g. Inspection"
              />
            </div>
            <div>
              {i === 0 && <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Internal key</label>}
              <input
                data-testid={`uc-field-key-${i}`}
                className="input-field"
                value={f.key || ""}
                onChange={(e) => update(i, { key: slugify(e.target.value) })}
                placeholder="inspection"
              />
            </div>
            <div>
              {i === 0 && <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4, whiteSpace: "nowrap" }}>Has time</label>}
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 4px", cursor: "pointer", fontSize: 13 }}>
                <input
                  type="checkbox"
                  data-testid={`uc-field-hastime-${i}`}
                  checked={!!f.hasTime}
                  onChange={(e) => update(i, { hasTime: e.target.checked })}
                  style={{ width: 16, height: 16, accentColor: "var(--primary)" }}
                />
              </label>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="btn-ghost" style={{ padding: 8 }} data-testid={`uc-field-up-${i}`}>
                <ChevronUp size={14} />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === fields.length - 1} className="btn-ghost" style={{ padding: 8 }} data-testid={`uc-field-down-${i}`}>
                <ChevronDown size={14} />
              </button>
              <button type="button" onClick={() => remove(i)} className="btn-ghost" style={{ padding: 8, color: "var(--secondary)" }} data-testid={`uc-field-remove-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {fields.length === 0 && (
          <div style={{ color: "var(--text-muted)", fontSize: 14, fontStyle: "italic" }}>
            No contract milestones yet — add your first one below.
          </div>
        )}
      </div>

      <button type="button" onClick={add} className="btn-secondary" data-testid="uc-field-add" style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Plus size={14} /> Add milestone
      </button>

      <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 18 }}>
        <b>Note:</b> the internal key links a milestone to the saved date on each seller's record. Renaming the <i>label</i> is safe; renaming the <i>key</i> on an existing milestone hides any saved values for that key. Removing a milestone here removes it from the form for ALL sellers — saved values stay in the database but won't be displayed.
      </p>
    </div>
  );
}


/* ---------- Date Types editor ---------- */
function DateTypesEditor({ draft, setDraft, field = "date_fields" }) {
  const fields = draft[field] || [];
  const update = (i, patch) => setDraft({ ...draft, [field]: fields.map((f, j) => j === i ? { ...f, ...patch } : f) });
  const remove = (i) => setDraft({ ...draft, [field]: fields.filter((_, j) => j !== i) });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, [field]: next });
  };
  const slugify = (label) => (label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || `date_${Date.now().toString(36).slice(-4)}`;
  const add = () => {
    const usedKeys = new Set(fields.map((f) => f.key));
    let baseKey = "new_date";
    let key = baseKey;
    let n = 2;
    while (usedKeys.has(key)) { key = `${baseKey}_${n}`; n += 1; }
    setDraft({ ...draft, [field]: [...fields, { key, label: "New Date" }] });
  };

  return (
    <div className="card" style={{ padding: 28, maxWidth: 760 }} data-testid="date-types-editor">
      <div className="overline">Date types</div>
      <p style={{ color: "var(--text-muted)", fontSize: 14, marginTop: 4, maxWidth: 600 }}>
        Add, rename, reorder, or remove the date options that appear in <b>Admin → Sellers → Key Dates</b> and on every seller's dashboard. Changes apply to all sellers.
      </p>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        {fields.map((f, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end" }} data-testid={`date-field-row-${i}`}>
            <div>
              {i === 0 && <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Display label</label>}
              <input
                data-testid={`date-field-label-${i}`}
                className="input-field"
                value={f.label || ""}
                onChange={(e) => {
                  const newLabel = e.target.value;
                  // Auto-keep the key in sync only if label was the auto-generated default ("New Date").
                  const shouldAutoKey = (f.label === "New Date" || !f.key);
                  update(i, { label: newLabel, ...(shouldAutoKey ? { key: slugify(newLabel) } : {}) });
                }}
                placeholder="e.g. Inspection"
              />
            </div>
            <div>
              {i === 0 && <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Internal key (a–z, 0–9, _)</label>}
              <input
                data-testid={`date-field-key-${i}`}
                className="input-field"
                value={f.key || ""}
                onChange={(e) => update(i, { key: slugify(e.target.value) })}
                placeholder="inspection"
              />
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="btn-ghost" style={{ padding: 8 }} data-testid={`date-field-up-${i}`}>
                <ChevronUp size={14} />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === fields.length - 1} className="btn-ghost" style={{ padding: 8 }} data-testid={`date-field-down-${i}`}>
                <ChevronDown size={14} />
              </button>
              <button type="button" onClick={() => remove(i)} className="btn-ghost" style={{ padding: 8, color: "var(--secondary)" }} data-testid={`date-field-remove-${i}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {fields.length === 0 && (
          <div style={{ color: "var(--text-muted)", fontSize: 14, fontStyle: "italic" }}>No date types yet — add your first one below.</div>
        )}
      </div>

      <button type="button" onClick={add} className="btn-secondary" data-testid="date-field-add" style={{ marginTop: 14, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Plus size={14} /> Add date type
      </button>

      <p style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 18 }}>
        <b>Note:</b> the internal key is what links a date to a seller's saved value. If you rename a key on an existing date, sellers' previously-saved dates for that key will no longer appear. Renaming the <i>label</i> is always safe.
      </p>
    </div>
  );
}


/* ---------- Timeline editor ---------- */
function TimelineEditor({ draft, setDraft, field = "timeline" }) {
  const stages = draft[field] || [];
  const update = (i, patch) => setDraft({ ...draft, [field]: stages.map((s, j) => j === i ? { ...s, ...patch } : s) });
  const remove = (i) => setDraft({ ...draft, [field]: stages.filter((_, j) => j !== i) });
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= stages.length) return;
    const next = [...stages]; [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, [field]: next });
  };
  const add = () => setDraft({ ...draft, [field]: [...stages, { id: `stage_${Date.now()}`, title: "New stage", blurb: "", actions: [] }] });

  const addAction = (i) => update(i, { actions: [...(stages[i].actions || []), "New action"] });
  const setAction = (i, k, v) => update(i, { actions: stages[i].actions.map((a, j) => j === k ? v : a) });
  const removeAction = (i, k) => update(i, { actions: stages[i].actions.filter((_, j) => j !== k) });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" className="btn-secondary" onClick={add} data-testid="stage-add"><Plus size={14} /> Add stage</button>
      </div>
      {stages.map((s, i) => (
        <div key={s.id || i} className="card" style={{ padding: 24 }} data-testid={`stage-edit-${i}`}>
          <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 30 }}>
              <button type="button" className="btn-ghost" style={{ padding: 2 }} onClick={() => move(i, -1)} disabled={i === 0}><ChevronUp size={14} /></button>
              <button type="button" className="btn-ghost" style={{ padding: 2 }} onClick={() => move(i, +1)} disabled={i === stages.length - 1}><ChevronDown size={14} /></button>
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <span className="overline">Stage {String(i + 1).padStart(2, "0")}</span>
                <button type="button" className="btn-ghost" onClick={() => remove(i)} data-testid={`stage-remove-${i}`}><Trash2 size={14} /></button>
              </div>
              <L label="Title"><input data-testid={`stage-title-${i}`} className="input-field" value={s.title || ""} onChange={(e) => update(i, { title: e.target.value })} /></L>
              <L label="Description"><textarea data-testid={`stage-blurb-${i}`} className="input-field" rows={2} value={s.blurb || ""} onChange={(e) => update(i, { blurb: e.target.value })} /></L>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={{ fontSize: 13, fontWeight: 500 }}>Action items</label>
                  <button type="button" className="btn-ghost" onClick={() => addAction(i)} data-testid={`stage-add-action-${i}`}><Plus size={14} /> Add</button>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                  {(s.actions || []).map((a, k) => (
                    <div key={k} style={{ display: "flex", gap: 6 }}>
                      <input className="input-field" value={a} onChange={(e) => setAction(i, k, e.target.value)} data-testid={`stage-action-${i}-${k}`} />
                      <button type="button" className="btn-ghost" onClick={() => removeAction(i, k)}><Trash2 size={14} /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Vendors editor ---------- */
function VendorsEditor({ draft, setDraft }) {
  const vendors = draft.vendors || [];
  const update = (i, patch) => setDraft({ ...draft, vendors: vendors.map((v, j) => j === i ? { ...v, ...patch } : v) });
  const remove = (i) => setDraft({ ...draft, vendors: vendors.filter((_, j) => j !== i) });
  const add = () => setDraft({ ...draft, vendors: [...vendors, { category: "Cleaning", name: "New vendor", phone: "", email: "" }] });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button type="button" className="btn-secondary" onClick={add} data-testid="vendor-add-btn"><Plus size={14} /> Add vendor</button>
      </div>
      {vendors.map((v, i) => (
        <div key={i} className="card" style={{ padding: 20 }} data-testid={`vendor-edit-${i}`}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
            <L label="Category"><input data-testid={`vendor-cat-${i}`} className="input-field" value={v.category || ""} onChange={(e) => update(i, { category: e.target.value })} /></L>
            <L label="Name"><input data-testid={`vendor-name-${i}`} className="input-field" value={v.name || ""} onChange={(e) => update(i, { name: e.target.value })} /></L>
            <L label="Phone"><input data-testid={`vendor-phone-${i}`} className="input-field" value={v.phone || ""} onChange={(e) => update(i, { phone: e.target.value })} /></L>
            <L label="Email"><input data-testid={`vendor-email-${i}`} className="input-field" value={v.email || ""} onChange={(e) => update(i, { email: e.target.value })} /></L>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="btn-ghost" onClick={() => remove(i)} data-testid={`vendor-remove-${i}`}><Trash2 size={14} /> Remove</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Closing editor ---------- */
function ClosingEditor({ draft, setDraft, field = "closing", title = "Closing-day checklist" }) {
  return (
    <ChecklistEditor
      title={title}
      testidPrefix={field === "buyer_closing" ? "buyer-closing" : "closing"}
      idPrefix="c"
      items={draft[field] || []}
      onChange={(items) => setDraft({ ...draft, [field]: items })}
    />
  );
}

/* ---------- Forms editor ---------- */
function FormsEditor({ draft, setDraft }) {
  const defaults = DEFAULT_CONTENT.forms?.listing_disclaimer || { enabled: true, title: "", body: "" };
  const ld = draft.forms?.listing_disclaimer || defaults;
  const set = (patch) => setDraft({
    ...draft,
    forms: { ...(draft.forms || {}), listing_disclaimer: { ...ld, ...patch } },
  });
  const reset = () => set({ title: defaults.title, body: defaults.body });
  return (
    <div className="card" style={{ padding: 28 }} data-testid="forms-editor">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Listing Input Form disclaimer</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>
            The friendly note that appears at the top of the seller's Listing Input Form. Use it to set expectations about the form's purpose and to remind sellers to verify any pre-filled fields.
          </p>
        </div>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, marginBottom: 20, cursor: "pointer" }}>
        <input
          data-testid="forms-disclaimer-enabled-toggle"
          type="checkbox"
          checked={!!ld.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
        />
        <span>Show this note at the top of the seller's Listing Input Form</span>
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
        <L label="Heading" hint="A short, warm intro line. Leave blank to hide the heading.">
          <input
            data-testid="forms-disclaimer-title"
            className="input-field"
            value={ld.title || ""}
            onChange={(e) => set({ title: e.target.value })}
            placeholder={defaults.title}
          />
        </L>
        <L label="Body" hint="The full message. Mention what the form is used for and that pre-filled fields should be verified.">
          <textarea
            data-testid="forms-disclaimer-body"
            className="input-field"
            rows={5}
            value={ld.body || ""}
            onChange={(e) => set({ body: e.target.value })}
            placeholder={defaults.body}
          />
        </L>
        <div>
          <button type="button" className="btn-ghost" onClick={reset} data-testid="forms-disclaimer-reset" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <RotateCcw size={14} /> Reset to default wording
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Buyer Welcome Info field editor ----------
   Shown during the under-contract phase. Each field has:
     • key   — internal identifier (auto-derived from label, but editable)
     • kind  — "short" (one-line input) or "long" (multi-line textarea)
     • label — what the seller sees
     • placeholder — example text inside the empty field
     • hint  — small note shown above long-form fields
   We render a compact editor with reorder + add/delete; mirrors the
   under-contract milestones editor on the Date Types tab. */
function BuyerInfoFieldsEditor({ draft, setDraft }) {
  const fields = draft.buyer_info_fields || [];
  const update = (i, patch) => setDraft({
    ...draft,
    buyer_info_fields: fields.map((f, j) => (j === i ? { ...f, ...patch } : f)),
  });
  const remove = (i) => setDraft({
    ...draft,
    buyer_info_fields: fields.filter((_, j) => j !== i),
  });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    setDraft({ ...draft, buyer_info_fields: next });
  };
  const slugify = (label) => (label || "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || `bi_${Date.now().toString(36).slice(-4)}`;
  const add = (kind) => {
    const usedKeys = new Set(fields.map((f) => f.key));
    let baseKey = kind === "long" ? "new_note" : "new_field";
    let key = baseKey;
    let n = 2;
    while (usedKeys.has(key)) { key = `${baseKey}_${n}`; n += 1; }
    const newField = { key, kind, label: kind === "long" ? "New note" : "New field", placeholder: "", hint: "" };
    setDraft({ ...draft, buyer_info_fields: [...fields, newField] });
  };
  const resetToDefaults = () => {
    if (!confirm("Reset the Buyer Welcome Info form to the built-in defaults? Your custom fields will be lost.")) return;
    setDraft({ ...draft, buyer_info_fields: DEFAULT_CONTENT.buyer_info_fields });
  };

  return (
    <div className="card" style={{ padding: 28, marginTop: 24 }} data-testid="buyer-info-fields-editor">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Buyer Welcome Info form</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0", maxWidth: 640 }}>
            The under-contract form sellers fill out so you have a clean info packet for the buyer at closing — trash day, garage code, alarm code, neighborhood tips. Add, rename, or remove fields here. <b>Short fields</b> render as a one-line input on a 2-column grid; <b>long fields</b> render as a full-width textarea with an optional hint.
          </p>
        </div>
        <button
          type="button"
          data-testid="buyer-info-fields-reset"
          onClick={resetToDefaults}
          className="btn-ghost"
          style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-muted)" }}
        >
          <RotateCcw size={13} /> Reset to defaults
        </button>
      </div>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        {fields.map((f, i) => (
          <div key={i} data-testid={`bi-field-row-${i}`} style={{
            background: "var(--surface-alt)", padding: 14, borderRadius: 10, border: "1px solid var(--border)",
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr) 110px auto", gap: 10, alignItems: "end" }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Display label</label>
                <input
                  data-testid={`bi-field-label-${i}`}
                  className="input-field"
                  value={f.label || ""}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    const shouldAutoKey = (f.label === "New field" || f.label === "New note" || !f.key);
                    update(i, { label: newLabel, ...(shouldAutoKey ? { key: slugify(newLabel) } : {}) });
                  }}
                  placeholder="e.g. Trash day"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Internal key</label>
                <input
                  data-testid={`bi-field-key-${i}`}
                  className="input-field"
                  value={f.key || ""}
                  onChange={(e) => update(i, { key: slugify(e.target.value) })}
                  placeholder="trash_day"
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)", display: "block", marginBottom: 4 }}>Kind</label>
                <select
                  data-testid={`bi-field-kind-${i}`}
                  className="input-field"
                  value={f.kind || "short"}
                  onChange={(e) => update(i, { kind: e.target.value })}
                >
                  <option value="short">Short</option>
                  <option value="long">Long</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" className="btn-ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`bi-field-up-${i}`} style={{ padding: "8px 10px" }} aria-label="Move up">↑</button>
                <button type="button" className="btn-ghost" onClick={() => move(i, +1)} disabled={i === fields.length - 1} data-testid={`bi-field-down-${i}`} style={{ padding: "8px 10px" }} aria-label="Move down">↓</button>
                <button type="button" className="btn-ghost" onClick={() => remove(i)} data-testid={`bi-field-remove-${i}`} style={{ padding: "8px 10px", color: "var(--secondary)" }} aria-label="Remove">×</button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <input
                data-testid={`bi-field-placeholder-${i}`}
                className="input-field"
                value={f.placeholder || ""}
                onChange={(e) => update(i, { placeholder: e.target.value })}
                placeholder="Placeholder shown inside the empty field"
              />
              <input
                data-testid={`bi-field-hint-${i}`}
                className="input-field"
                value={f.hint || ""}
                onChange={(e) => update(i, { hint: e.target.value })}
                placeholder="(Optional) hint shown above the field"
              />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button type="button" className="btn-ghost" onClick={() => add("short")} data-testid="bi-field-add-short" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
          + Add short field
        </button>
        <button type="button" className="btn-ghost" onClick={() => add("long")} data-testid="bi-field-add-long" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
          + Add long-form note
        </button>
      </div>
    </div>
  );
}



/* ---------- Resources editor (buy-side only) ---------- */
function ResourcesEditor({ draft, setDraft }) {
  const items = draft.buyer_resources || [];
  const set = (next) => setDraft({ ...draft, buyer_resources: next });
  const update = (i, patch) => set(items.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const add = () => set([...items, { title: "", url: "", description: "" }]);
  const remove = (i) => set(items.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    set(next);
  };
  return (
    <div className="card" style={{ padding: 28 }} data-testid="resources-editor">
      <div style={{ marginBottom: 22 }}>
        <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Buyer resources</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>
          Curated articles, videos, and guides for buyers. Shown as cards on the Resources tab of their portal.
        </p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {items.length === 0 && (
          <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "12px 0" }}>No resources yet. Click &ldquo;Add resource&rdquo; to add your first.</div>
        )}
        {items.map((r, i) => (
          <div key={i} data-testid={`resource-row-${i}`} className="card" style={{ padding: 18, background: "var(--surface-alt)", border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <span className="overline">Resource {String(i + 1).padStart(2, "0")}</span>
              <div style={{ display: "flex", gap: 4 }}>
                <button type="button" className="btn-ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`resource-up-${i}`} style={{ padding: 4 }}><ChevronUp size={14} /></button>
                <button type="button" className="btn-ghost" onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`resource-down-${i}`} style={{ padding: 4 }}><ChevronDown size={14} /></button>
                <button type="button" className="btn-ghost" onClick={() => remove(i)} data-testid={`resource-delete-${i}`} style={{ padding: 4, color: "#a14040" }}><Trash2 size={14} /></button>
              </div>
            </div>
            <div style={{ display: "grid", gap: 12 }}>
              <L label="Title">
                <input data-testid={`resource-title-${i}`} className="input-field" value={r.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="First-time homebuyer guide" />
              </L>
              <L label="URL">
                <input data-testid={`resource-url-${i}`} className="input-field" type="url" value={r.url || ""} onChange={(e) => update(i, { url: e.target.value })} placeholder="https://…" />
              </L>
              <L label="Description">
                <textarea data-testid={`resource-desc-${i}`} className="input-field" rows={2} value={r.description || ""} onChange={(e) => update(i, { description: e.target.value })} placeholder="One-line hook so buyers know what they'll get when they click through." />
              </L>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="btn-secondary" onClick={add} data-testid="resource-add-btn" style={{ marginTop: 18, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Plus size={14} /> Add resource
      </button>
    </div>
  );
}


/* ---------- Buyer dashboard copy editor (buy-side only) ----------
   Everything the buyer reads on their dashboard home: the greeting subtitle,
   the "Up Next" prompt, the all-forms-done message, and the 4 Quick Access
   cards. Reads/writes content.buyer_dashboard. */
function DashboardCopyEditor({ draft, setDraft }) {
  const d = draft.buyer_dashboard || JSON.parse(JSON.stringify(DEFAULT_CONTENT.buyer_dashboard));
  const set = (patch) => setDraft({ ...draft, buyer_dashboard: { ...d, ...patch } });
  const qa = (d.quick_access && d.quick_access.length === 4) ? d.quick_access : DEFAULT_CONTENT.buyer_dashboard.quick_access;
  const setQa = (i, patch) => set({ quick_access: qa.map((c, j) => j === i ? { ...c, ...patch } : c) });

  const Field = ({ label, value, onChange, area, hint }) => (
    <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 16 }}>
      {label}
      {hint && <span style={{ color: "var(--text-muted)", fontWeight: 400, marginLeft: 8 }}>{hint}</span>}
      {area ? (
        <textarea className="input-field" style={{ marginTop: 6, minHeight: 70 }} value={value} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className="input-field" style={{ marginTop: 6 }} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );

  return (
    <div className="card" style={{ padding: 28 }} data-testid="dashboard-copy-editor">
      <div style={{ marginBottom: 18 }}>
        <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Dashboard</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0", maxWidth: 620 }}>
          Every line of copy on a buyer&rsquo;s dashboard home. The progress numbers and stage count fill in automatically.
        </p>
      </div>

      <span className="overline">Greeting</span>
      <div style={{ marginTop: 12, marginBottom: 20 }}>
        <div data-testid="dash-edit-subtitle"><Field label="Subtitle (under “Hello, [name].”)" value={d.subtitle} onChange={(v) => set({ subtitle: v })} area /></div>
      </div>

      <span className="overline">Card labels</span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginTop: 12, marginBottom: 20 }}>
        <div data-testid="dash-edit-forms-label"><Field label="Intake forms card" value={d.card_forms_label || ""} onChange={(v) => set({ card_forms_label: v })} /></div>
        <div data-testid="dash-edit-process-label"><Field label="Buyer process card" value={d.card_process_label || ""} onChange={(v) => set({ card_process_label: v })} /></div>
        <div data-testid="dash-edit-upnext-label"><Field label="Up next card" value={d.card_upnext_label || ""} onChange={(v) => set({ card_upnext_label: v })} /></div>
      </div>

      <span className="overline">“Up Next” card</span>
      <div style={{ marginTop: 12, marginBottom: 20 }}>
        <div data-testid="dash-edit-upnext"><Field label="Prompt while forms are unfinished" hint="shown under “Finish your … form”" value={d.upnext_note} onChange={(v) => set({ upnext_note: v })} area /></div>
        <div data-testid="dash-edit-alldone-title"><Field label="Heading once both forms are in" value={d.alldone_title} onChange={(v) => set({ alldone_title: v })} /></div>
        <div data-testid="dash-edit-alldone-note"><Field label="Message once both forms are in" value={d.alldone_note} onChange={(v) => set({ alldone_note: v })} area /></div>
      </div>

      <span className="overline">Quick access cards</span>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 12 }}>
        {qa.map((c, i) => (
          <div key={i} data-testid={`dash-edit-qa-${i}`} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 14, background: "#FBFAF6" }}>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>Card {i + 1} · links to {["Buyer Process", "Calculators", "Forms", "Vendors"][i]}</div>
            <Field label="Title" value={c.title} onChange={(v) => setQa(i, { title: v })} />
            <label style={{ display: "block", fontSize: 13, fontWeight: 500 }}>
              Sub-copy
              <input className="input-field" style={{ marginTop: 6 }} value={c.sub} onChange={(e) => setQa(i, { sub: e.target.value })} />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Buyer intake forms editor (buy-side only) ----------
   Lets the agent add/remove/reorder/rename sections AND the questions
   inside them for both buyer intake forms, and pick each question's type.
   Reads/writes content.buyer_intake_sections and buyer_preferences_sections. */

function IntakeFormsEditor({ draft, setDraft }) {
  const intakeSections = draft.buyer_intake_sections || [];
  const prefSections = draft.buyer_preferences_sections || [];
  const resetIntake = () => {
    if (!window.confirm("Reset the \u201cGet to know you\u201d form to the built-in defaults? Your custom questions will be lost.")) return;
    setDraft({ ...draft, buyer_intake_sections: JSON.parse(JSON.stringify(DEFAULT_CONTENT.buyer_intake_sections)) });
  };
  const resetPref = () => {
    if (!window.confirm("Reset the \u201cWhat you're looking for\u201d form to the built-in defaults? Your custom questions will be lost.")) return;
    setDraft({ ...draft, buyer_preferences_sections: JSON.parse(JSON.stringify(DEFAULT_CONTENT.buyer_preferences_sections)) });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="intake-forms-editor">
      <SectionedFormEditor
        formKey="intake"
        title={'"Get to know you" intake form'}
        intro="The first form new buyers fill out. Rename or reorder the section headers, add/remove questions, and pick each question's type. Changes go live the next time a buyer opens the form."
        sections={intakeSections}
        onChange={(next) => setDraft({ ...draft, buyer_intake_sections: next })}
        onReset={resetIntake}
      />
      <SectionedFormEditor
        formKey="preferences"
        title={'"What you\'re looking for" preferences form'}
        intro="Budget, timeline, must-haves and dealbreakers. Same editing controls — tweak the questions to match how you like to qualify buyers."
        sections={prefSections}
        onChange={(next) => setDraft({ ...draft, buyer_preferences_sections: next })}
        onReset={resetPref}
      />
    </div>
  );
}



/* ---------- Lender editor (buy-side only) ---------- */
function LenderEditor({ draft, setDraft }) {
  const defaults = DEFAULT_CONTENT.lender || {};
  const l = draft.lender || defaults;
  const set = (patch) => setDraft({ ...draft, lender: { ...l, ...patch } });
  return (
    <div className="card" style={{ padding: 28 }} data-testid="lender-editor">
      <div style={{ marginBottom: 22 }}>
        <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Preferred lender</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>
          Shown as a contact card at the top of your buyer&rsquo;s cost calculator. Leave any field blank to hide it in the card.
        </p>
      </div>

      {/* Live preview so admins see exactly what buyers see */}
      <div style={{ marginBottom: 24, padding: 16, borderRadius: 12, background: "var(--surface-alt)", border: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="lender-preview">
        <div>
          <div className="overline">Your preferred lender</div>
          <div style={{ fontWeight: 600, marginTop: 4 }}>{l.name || defaults.name}</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
            {l.title || defaults.title}{(l.title || defaults.title) && (l.company || defaults.company) ? " · " : ""}{l.company || defaults.company}
            {l.nmls ? ` · NMLS ${l.nmls}` : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 13 }}>
          {(l.phone || defaults.phone) && <span style={{ color: "var(--primary)" }}>{l.phone || defaults.phone}</span>}
          {(l.email || defaults.email) && <span style={{ color: "var(--primary)" }}>{l.email || defaults.email}</span>}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
        <L label="Loan officer name">
          <input data-testid="lender-name" className="input-field" value={l.name || ""} onChange={(e) => set({ name: e.target.value })} placeholder={defaults.name} />
        </L>
        <L label="Title" hint="e.g. Loan Officer, Senior Mortgage Advisor">
          <input data-testid="lender-title" className="input-field" value={l.title || ""} onChange={(e) => set({ title: e.target.value })} placeholder={defaults.title} />
        </L>
        <L label="Company / brokerage">
          <input data-testid="lender-company" className="input-field" value={l.company || ""} onChange={(e) => set({ company: e.target.value })} placeholder={defaults.company} />
        </L>
        <L label="NMLS ID" hint="Nationwide Mortgage Licensing System — required disclosure on lender-facing materials.">
          <input data-testid="lender-nmls" className="input-field" value={l.nmls || ""} onChange={(e) => set({ nmls: e.target.value })} placeholder={defaults.nmls} />
        </L>
        <L label="Phone">
          <input data-testid="lender-phone" className="input-field" type="tel" value={l.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder={defaults.phone} />
        </L>
        <L label="Email">
          <input data-testid="lender-email" className="input-field" type="email" value={l.email || ""} onChange={(e) => set({ email: e.target.value })} placeholder={defaults.email} />
        </L>
      </div>
    </div>
  );
}


/* ---------- Referrals editor ---------- */
function ReferralsEditor({ draft, setDraft }) {
  const r = draft.referrals || { enabled: true, headline: "", message: "", google_url: "", zillow_url: "" };
  const set = (patch) => setDraft({ ...draft, referrals: { ...r, ...patch } });
  return (
    <div className="card" style={{ padding: 28 }} data-testid="referrals-editor">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 18 }}>
        <div>
          <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Post-closing referral moment</h2>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: "4px 0 0" }}>
            After the seller's closing date, their dashboard shows a celebratory card asking for a Zillow or Google review. Personal. Timely. Un-pushy.
          </p>
        </div>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, marginBottom: 20, cursor: "pointer" }}>
        <input
          data-testid="referrals-enabled-toggle"
          type="checkbox"
          checked={!!r.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
        />
        <span>Show the referral card to sellers after their closing date</span>
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
        <L label="Headline">
          <input
            data-testid="referrals-headline"
            className="input"
            value={r.headline || ""}
            onChange={(e) => set({ headline: e.target.value })}
            placeholder="Thank you — truly."
          />
        </L>
        <L label="Message" hint="A warm note. You can mention how much it means.">
          <textarea
            data-testid="referrals-message"
            className="input"
            rows={4}
            value={r.message || ""}
            onChange={(e) => set({ message: e.target.value })}
            placeholder="It was a joy guiding you through your sale…"
          />
        </L>
        <L label="Google review URL" hint="Your Google Business review link. Leave blank to hide the Google button.">
          <input
            data-testid="referrals-google-url"
            className="input"
            value={r.google_url || ""}
            onChange={(e) => set({ google_url: e.target.value })}
            placeholder="https://g.page/r/…"
          />
        </L>
        <L label="Zillow profile URL" hint="Your Zillow agent profile. Leave blank to hide the Zillow button.">
          <input
            data-testid="referrals-zillow-url"
            className="input"
            value={r.zillow_url || ""}
            onChange={(e) => set({ zillow_url: e.target.value })}
            placeholder="https://www.zillow.com/profile/…"
          />
        </L>
      </div>
    </div>
  );
}


/* ---------- Emails editor ----------
   Two admin-editable fields on the seller-invite email (subject + intro
   + button label). Everything else (product name, agent name / brokerage /
   phone / email in the signature line) is auto-composed from Branding —
   so agents change those in one place and every email updates.
   Live preview panel on the right so agents can see the result inline. */
function EmailsEditor({ draft, setDraft }) {
  const emails = draft.emails || {};
  const invite = emails.seller_invite || {};

  const set = (key, value) => {
    setDraft((d) => ({
      ...d,
      emails: {
        ...(d.emails || {}),
        seller_invite: { ...((d.emails || {}).seller_invite || {}), [key]: value },
      },
    }));
  };

  const branding = draft.branding || {};
  const productName = (branding.product_name || "").trim() || "PorchLight";
  const agentName = (branding.agent_name || "").trim() || "Your agent";
  const firstName = "Jane"; // preview placeholder — sellers see their real first name
  const fullName = "Jane Miller";
  const fill = (tpl, fallback) =>
    ((tpl || "").trim() || fallback)
      .replaceAll("{product_name}", productName)
      .replaceAll("{first_name}", firstName)
      .replaceAll("{agent_name}", agentName)
      .replaceAll("{name}", fullName);

  const previewSubject = fill(invite.subject, `Welcome to ${productName}, ${firstName}!`);
  const previewIntro = fill(
    invite.intro,
    `${agentName} has set up your personal home-selling hub. To get started, activate your account:`
  );
  const previewButton = fill(invite.button_label, "Activate my account");
  const contactBits = [branding.firm, branding.agent_phone, branding.agent_email].filter(Boolean);
  const previewSignature = agentName ? [agentName, ...contactBits].join(" · ") : contactBits.join(" · ");

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, alignItems: "start" }}>
      {/* Left: editor */}
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <h3 className="font-serif" style={{ fontSize: 22, fontWeight: 500, margin: "0 0 6px" }}>
            Seller invite email
          </h3>
          <p style={{ color: "var(--text-muted)", fontSize: 14, margin: 0 }}>
            The email sellers receive when you add them. Product name and your contact info come from Branding — everything below is the copy in between.
          </p>
        </div>

        <L
          label="Subject line"
          hint="Placeholders: {product_name}, {first_name}. Leave blank for default."
        >
          <input
            data-testid="email-invite-subject"
            className="input-field"
            placeholder={`Welcome to ${productName}, {first_name}!`}
            value={invite.subject || ""}
            onChange={(e) => set("subject", e.target.value)}
          />
        </L>

        <L
          label="Intro paragraph"
          hint="Placeholders: {agent_name}, {product_name}. The body opens with 'Welcome to {product_name}, {name}!' — this paragraph appears right after."
        >
          <textarea
            data-testid="email-invite-intro"
            className="input-field"
            rows={3}
            placeholder={`{agent_name} has set up your personal home-selling hub. To get started, activate your account:`}
            value={invite.intro || ""}
            onChange={(e) => set("intro", e.target.value)}
            style={{ resize: "vertical", fontFamily: "inherit" }}
          />
        </L>

        <L label="Button label" hint="The call-to-action button that opens the activation link.">
          <input
            data-testid="email-invite-button-label"
            className="input-field"
            placeholder="Activate my account"
            value={invite.button_label || ""}
            onChange={(e) => set("button_label", e.target.value)}
          />
        </L>

        <div style={{ padding: "12px 14px", background: "var(--surface-alt)", borderRadius: 10, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>
          <strong style={{ color: "var(--text)" }}>Auto-filled from Branding:</strong> product name ({productName}), your name ({agentName || "—"}), brokerage, phone, email in the signature line. Change them in the Branding tab.
        </div>
      </div>

      {/* Right: live preview */}
      <div data-testid="email-invite-preview" style={{ position: "sticky", top: 24 }}>
        <div className="overline" style={{ marginBottom: 8 }}>Preview</div>
        <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: 12, padding: 20, boxShadow: "0 8px 24px rgba(28,25,23,0.05)" }}>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 12, paddingBottom: 12, borderBottom: "1px solid var(--border)" }}>
            <div><strong>Subject:</strong> {previewSubject}</div>
            <div><strong>From:</strong> {branding.sender_email || "your PorchLight sender"}</div>
          </div>
          <div style={{ fontFamily: "Arial, sans-serif", color: "#1C1917", maxWidth: 520 }}>
            <h2 style={{ color: "#2B4C3F", marginBottom: 4, fontSize: 22, fontWeight: 600 }}>
              Welcome to {productName}, {fullName}!
            </h2>
            <p style={{ color: "#57534E", fontSize: 14, lineHeight: 1.6 }}>{previewIntro}</p>
            <p style={{ margin: "24px 0" }}>
              <span style={{ background: "#2B4C3F", color: "#fff", padding: "10px 20px", borderRadius: 9999, fontWeight: 500, fontSize: 14, display: "inline-block" }}>
                {previewButton}
              </span>
            </p>
            <p style={{ color: "#57534E", fontSize: 12 }}>Or copy and paste this link:<br />
              <span style={{ color: "#2B4C3F" }}>https://…/invite/…</span>
            </p>
            <p style={{ color: "#57534E", fontSize: 12, marginTop: 20 }}>This link expires in 30 days.</p>
            <p style={{ color: "#57534E", fontSize: 12, marginTop: 20 }}>— {previewSignature || "—"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ---------- small helper ---------- */
function L({ label, children, hint, style }) {
  return (
    <div style={style}>
      <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
